import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import BlogAPI from "../api/blog";
import SEO from "../components/SEO";
import { useSchemaMicrodata } from '../hooks/useSchemaMicrodata';
import RelatedArticles from "../components/blog/RelatedArticles";
import AuthorBio from "../components/blog/AuthorBio";
import ShareButtons from "../components/blog/ShareButtons";
import TableOfContents from "../components/blog/TableOfContents";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useSchemaMicrodata('article', post ? {
    headline: post.title,
    description: post.excerpt,
    author: post.author?.name,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    image: post.featuredImage,
    url: window.location.href
  } : null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setIsLoading(true);
      const postData = await BlogAPI.getPostBySlug(slug);
      setPost(postData);

      // Fetch related posts
      if (postData._id) {
        const related = await BlogAPI.getRelatedPosts(postData._id, 3);
        setRelatedPosts(related || []);
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
      setPost(null);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 mt-[90px] max-md:mt-[40px] max-w-4xl">
          <Skeleton height={40} className="mb-4" />
          <Skeleton count={1} height={20} className="mb-8" />
          <Skeleton height={400} className="mb-8" />
          <Skeleton count={10} />
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <div className="container mx-auto px-6 mt-[90px] text-center">
          <h1 className="text-36 font-medium text-secondary mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-primary hover:underline">
            Back to Blog
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title={`${post.title} - Pharmacy Wiki Blog`}
        description={post.excerpt}
        keywords={post.tags?.join(', ')}
        image={post.featuredImage}
        article={true}
        publishedTime={post.publishedAt}
        modifiedTime={post.updatedAt}
        author={post.author?.name}
      />

      <article className="container mx-auto px-6 mt-[90px] max-md:mt-[40px]">
        {/* Breadcrumb */}
        <nav className="mb-6 text-14 text-light-gray">
          <Link to="/" className="hover:text-primary">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/blog" className="hover:text-primary">Blog</Link>
          {post.category && (
            <>
              <span className="mx-2">/</span>
              <Link to={`/blog/category/${post.category.slug}`} className="hover:text-primary">
                {post.category.name}
              </Link>
            </>
          )}
        </nav>

        <div className="max-w-4xl mx-auto">
          {/* Category Badge */}
          {post.category && (
            <Link to={`/blog/category/${post.category.slug}`}>
              <span className="inline-block px-3 py-1 text-12 font-medium text-primary bg-primary/10 rounded-full mb-4 hover:bg-primary/20 transition-colors">
                {post.category.name}
              </span>
            </Link>
          )}

          {/* Title */}
          <h1 className="text-48 max-md:text-32 font-medium text-secondary leading-tight mb-4">
            {post.title}
          </h1>

          {/* Meta Info */}
          <div className="flex items-center gap-4 text-14 text-light-gray mb-8 flex-wrap">
            {post.author && (
              <div className="flex items-center gap-2">
                {post.author.avatar && (
                  <img
                    src={post.author.avatar}
                    alt={post.author.name}
                    className="w-8 h-8 rounded-full"
                  />
                )}
                <span>By {post.author.name}</span>
              </div>
            )}
            <span>•</span>
            <time dateTime={post.publishedAt}>
              {new Date(post.publishedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </time>
            {post.readTime && (
              <>
                <span>•</span>
                <span>{post.readTime} min read</span>
              </>
            )}
          </div>

          {/* Featured Image */}
          {post.featuredImage && (
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-auto object-cover"
              />
            </div>
          )}

          {/* Medical Disclaimer */}
          {post.requiresDisclaimer && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
              <p className="text-14 text-secondary">
                <strong>Medical Disclaimer:</strong> This article is for informational purposes only and should not be considered medical advice. Always consult with a qualified healthcare professional before making any decisions about your health or medications.
              </p>
            </div>
          )}

          {/* Table of Contents */}
          {post.tableOfContents && post.tableOfContents.length > 0 && (
            <TableOfContents items={post.tableOfContents} />
          )}

          {/* Content */}
          <div className="prose prose-lg max-w-none mb-12">
            {typeof post.content === 'function' ? <post.content /> : (
              <div dangerouslySetInnerHTML={{ __html: post.content }} />
            )}
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 text-12 bg-light-gray/10 text-secondary rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Last Updated */}
          {post.updatedAt && post.updatedAt !== post.publishedAt && (
            <p className="text-12 text-light-gray mb-8">
              Last updated: {new Date(post.updatedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          )}

          {/* Share Buttons */}
          <ShareButtons
            url={window.location.href}
            title={post.title}
            description={post.excerpt}
          />

          {/* Author Bio */}
          {post.author && (
            <AuthorBio author={post.author} />
          )}

          {/* Related Articles */}
          {relatedPosts.length > 0 && (
            <RelatedArticles posts={relatedPosts} />
          )}
        </div>
      </article>
    </Layout>
  );
}
